import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { useAuth } from '@/lib/auth/use-auth'
import { supabase, toAppError } from '@/lib/db/supabase'
import { inviteRepository } from '@/modules/settings/invite-repository'
import {
  ORG_STORAGE_KEY,
  OrganizationContext,
  type Organization,
  type OrgRole,
} from './org-context'

interface MembershipRow {
  role: OrgRole
  organizations: {
    id: string
    name: string
    book_start_date: string | null
    currency_code: string | null
  } | null
}

async function fetchOrganizations(userId: string): Promise<Organization[]> {
  // One round trip: memberships embedded with their organization.
  //
  // FILTERING BY user_id IS NOT OPTIONAL. 0006's organization_members_select
  // policy is `app.is_member(organization_id)` — deliberately, so every member
  // can render the team roster — which means this query returns ONE ROW PER
  // MEMBER of every org the caller belongs to, not one row per organization.
  // Without the filter, inviting a second person made their company appear twice
  // in the switcher (same id, so both rows even rendered as "current"), and
  // `role` was read from whichever member's row came back first — a viewer could
  // be handed an owner's row and see controls they cannot use.
  //
  // The FY start month is not selected: the schema has no such column on purpose
  // (every FY helper hard-codes April), so it is a constant here rather than a
  // phantom field.
  const { data, error } = await supabase
    .from('organization_members')
    .select('role, organizations(id, name, book_start_date, currency_code)')
    .eq('user_id', userId)
    .order('created_at', { ascending: true })

  if (error) throw new Error(toAppError(error).message)

  return ((data ?? []) as unknown as MembershipRow[])
    .filter(
      (
        row,
      ): row is MembershipRow & { organizations: NonNullable<MembershipRow['organizations']> } =>
        row.organizations !== null,
    )
    .map((row) => ({
      id: row.organizations.id,
      name: row.organizations.name,
      role: row.role,
      bookStartDate: row.organizations.book_start_date,
      currencyCode: row.organizations.currency_code ?? 'INR',
    }))
}

/** A hung RPC must not become a permanent splash screen. */
const CLAIM_TIMEOUT_MS = 4000

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth()
  const queryClient = useQueryClient()
  const [selectedId, setSelectedId] = useState<string | null>(() => {
    try {
      return localStorage.getItem(ORG_STORAGE_KEY)
    } catch {
      return null
    }
  })

  const {
    data: organizations,
    isPending,
    error,
  } = useQuery({
    queryKey: ['organizations', user?.id ?? null],
    queryFn: () => fetchOrganizations(user!.id),
    enabled: Boolean(user),
    // Membership rarely changes, and a refetch on every window focus would
    // re-run on every tab switch for no benefit.
    staleTime: 5 * 60_000,
  })

  const list = useMemo(() => organizations ?? [], [organizations])

  // Fall back to the first org when the stored id is stale — a user removed
  // from an org must not be stranded on a workspace they can no longer read.
  const organization = useMemo(() => {
    if (list.length === 0) return null
    return list.find((org) => org.id === selectedId) ?? list[0] ?? null
  }, [list, selectedId])

  useEffect(() => {
    if (!organization) return
    if (organization.id === selectedId) return
    setSelectedId(organization.id)
  }, [organization, selectedId])

  useEffect(() => {
    if (!organization) return
    try {
      localStorage.setItem(ORG_STORAGE_KEY, organization.id)
    } catch {
      // Preference will not persist; the session still works.
    }
  }, [organization])

  // Keep the SERVER's active org in lockstep with the one selected here.
  //
  // Every insert defaults `organization_id` to `app.current_org()`, which reads
  // `profiles.default_organization_id`. Reads do NOT go through it — they pass
  // `p_org` explicitly — so the two can disagree silently: the screen shows one
  // company while every voucher saved from it is written into another.
  //
  // That is what happened. Selecting a company only set React state, and the
  // sync below was fire-and-forget: on a fresh load the stored preference could
  // be company B while the server still said company A, and anything saved in
  // that window either failed a composite foreign key —
  //   Key (contact_id, organization_id)=(…) is not present in table "contacts"
  // — or, when the row happened to satisfy it, posted into the wrong company's
  // books with nothing to show for it.
  //
  // So the confirmed org is now STATE, and the app is not interactive until it
  // matches the selection. Awaiting one small request beats letting a voucher
  // land in the wrong tenant.
  const userId = user?.id
  const activeOrgId = organization?.id
  const [syncedOrgId, setSyncedOrgId] = useState<string | null>(null)
  const [syncError, setSyncError] = useState<string | null>(null)

  useEffect(() => {
    if (!userId || !activeOrgId) return
    if (syncedOrgId === activeOrgId) return
    let cancelled = false
    void (async () => {
      try {
        const { error: updateError } = await supabase
          .from('profiles')
          .update({ default_organization_id: activeOrgId })
          .eq('id', userId)
        if (cancelled) return
        if (updateError) {
          setSyncError(toAppError(updateError).message)
          return
        }
        setSyncError(null)
        setSyncedOrgId(activeOrgId)
      } catch (caught) {
        if (!cancelled) setSyncError(toAppError(caught).message)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [userId, activeOrgId, syncedOrgId])

  // Never report a company as active until the server agrees it is the one being
  // written to. `syncError` releases the gate deliberately: a user who cannot
  // reach the server should still be able to READ their books, and the error
  // below tells them why saving is refused.
  const orgConfirmed = Boolean(activeOrgId) && syncedOrgId === activeOrgId

  // Claim any invitation addressed to this user before concluding they have no
  // workspace at all.
  //
  // The database triggers are the primary path; this covers the cases they
  // cannot — an account that predates the feature, a confirmation that took a
  // route we did not anticipate, or an address changed to the invited one.
  //
  // It runs once per signed-in user and is NOT in the global `loading` gate:
  // putting an RPC in front of every boot, forever, to serve a rare recovery
  // path would make a stalled connection into a permanent spinner for everyone.
  // Only the zero-organizations case waits on it (see `loading` below), because
  // that is the only case whose ANSWER can change.
  const [claimSettled, setClaimSettled] = useState(false)
  useEffect(() => {
    if (!userId) {
      setClaimSettled(true)
      return
    }
    let active = true
    setClaimSettled(false)
    void (async () => {
      try {
        const claimed = await Promise.race([
          inviteRepository.claimMine(),
          new Promise<number>((_, reject) =>
            setTimeout(() => reject(new Error('claim timed out')), CLAIM_TIMEOUT_MS),
          ),
        ])
        if (active && claimed > 0) {
          await queryClient.invalidateQueries({ queryKey: ['organizations', userId] })
        }
      } catch {
        // Best-effort by design. The triggers are the real path, and a failure
        // here must never take down the provider that renders the whole app.
      } finally {
        if (active) setClaimSettled(true)
      }
    })()
    return () => {
      active = false
    }
  }, [userId, queryClient])

  const switchOrganization = useCallback((id: string) => {
    setSelectedId(id)
  }, [])

  const value = useMemo(
    () => ({
      organization,
      organizations: list,
      switchOrganization,
      // The extra arm holds the splash ONLY for a signed-in user with no
      // organizations while a claim is still in flight. Without it their org
      // query returns [] first, the onboarding screen renders, and they create
      // a junk company one keystroke before their real one appears.
      loading:
        authLoading ||
        (Boolean(user) && isPending) ||
        (Boolean(user) && !isPending && list.length === 0 && !claimSettled) ||
        // Hold the splash until the server is writing to the company on screen.
        (Boolean(user) && list.length > 0 && !orgConfirmed && !syncError),
      error: error ? toAppError(error).message : syncError,
      needsOnboarding: Boolean(user) && !isPending && claimSettled && list.length === 0,
    }),
    [
      organization,
      list,
      switchOrganization,
      authLoading,
      user,
      isPending,
      claimSettled,
      error,
      orgConfirmed,
      syncError,
    ],
  )

  return <OrganizationContext value={value}>{children}</OrganizationContext>
}
